import { useContext, useState } from "react";
import api from "../../api";
import { useToast } from "../ToastContext";
import { LoadingContext } from "../LoadingContext";
import { AuthContext } from "../AuthContext";
import { TerceiroType } from "./useTerceiro";

export interface GerenteFotografoType {
    id: number;
    gerenteId: number;
    gerenteName: string;
    fotografoId: number;
    fotografoName: string;
}

export interface CreateGerenteFotografoType {
    empresaId: number;
    gerenteId: number;
    fotografoId: number[];
}

export interface UpdateGerenteFotografoType {
    id: number;
    gerenteId: number;
    fotografoId: number;
}

export interface CreateGerenteFotografoResponse {
    mensagem: string;
    relacoes: GerenteFotografoType[];
    erros?: string[] | null;
}

export default function useGerenteFotografo() {
    const showToast = useToast();
    const { setLoading } = useContext(LoadingContext);
    const { handleLogout, objUserTk } = useContext(AuthContext);
    const [listGerenteFotografo, setListGerenteFotografo] = useState<GerenteFotografoType[]>([]);
    const [listGerenteFotografoByEmpresa, setListGerenteFotografoByEmpresa] = useState<GerenteFotografoType[]>([]);
    const [gerenteFotografoPageCount, setGerenteFotografoPageCount] = useState<number>(0);
    const [initialListGerenteFotografo, setInitialListGerenteFotografo] = useState<GerenteFotografoType[]>([]);
    const [gerenteFotografo, setGerenteFotografo] = useState<GerenteFotografoType>({
        id: 0,
        gerenteId: 0,
        gerenteName: "",
        fotografoId: 0,
        fotografoName: ""
    });

    function createGerenteFotografo(createGerenteFotografoType: CreateGerenteFotografoType): Promise<CreateGerenteFotografoResponse | null> {
        setLoading(true);
        return api
            .post(`/GerenteFotografo/create`, createGerenteFotografoType)
            .then((response) => {
                const data = response.data as CreateGerenteFotografoResponse;
                showToast("success", data.mensagem || "Relações criadas com sucesso.");
                setLoading(false);
                return data;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast("error", "Sessão desconectada. Favor login novamente.", error);
                } else {
                    const erroMsg = error?.response?.data?.mensagem || "Erro ao criar relação. Verifique novamente mais tarde.";
                    showToast("error", erroMsg, error);
                }
                setLoading(false);
                console.error("Erro ao criar relação:", error);
                return null;
            });
    }

    function getAllGerenteFotografoPaged(
        pageNumber: number,
        pageSize: number,
        empresaId?: number,
    ): Promise<GerenteFotografoType[] | null> {
        setLoading(true);
        let url = `/GerenteFotografo/getAllGerenteFotografoPaged?pageNumber=${pageNumber}&pageSize=${pageSize}`;

        if (empresaId) {
            url += `&empresaId=${empresaId}`;
        }

        return api
            .get(url)
            .then((response) => {
                setListGerenteFotografo(response.data.result);
                setInitialListGerenteFotografo(response.data.result);
                setGerenteFotografoPageCount(response.data.pageCount);
                setLoading(false);
                return response.data.result;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao carregar relações de gerente com fotógrafo. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao carregar relações de gerente com fotógrafo:", error);
                return null;
            });
    }

    function updateGerenteFotografo(updateGerenteFotografoData: UpdateGerenteFotografoType): Promise<UpdateGerenteFotografoType | null> {
        setLoading(true);
        return api
            .put(`/GerenteFotografo/${updateGerenteFotografoData.id}`, updateGerenteFotografoData)
            .then((response) => {
                showToast("success", "Relação de gerente com fotógrafo atualizada com sucesso.");
                setLoading(false);
                return response.data;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao atualizar relação de gerente com fotógrafo. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao atualizar relação de gerente com fotógrafo:", error);
                return null;
            });
    }

    function deleteGerenteFotografo(gerenteFotografoId: number): Promise<GerenteFotografoType | null> {
        setLoading(true);
        return api
            .delete(`/GerenteFotografo/${gerenteFotografoId}`)
            .then(() => {
                setGerenteFotografo({
                    id: 0,
                    gerenteId: 0,
                    gerenteName: "",
                    fotografoId: 0,
                    fotografoName: "",
                });
                showToast("success", "Relação de gerente com fotógrafo deletada com sucesso.");
                setLoading(false);
                return null;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao deletar relação de gerente com fotógrafo. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao deletar relação de gerente com fotógrafo:", error);
                return null;
            });
    }

    function handleGetGerenteFotografoByEmpresaId(empresaId: number): Promise<GerenteFotografoType[] | null> {
        setLoading(true);
        return api
            .get(`/GerenteFotografo/getGerenteFotografoByEmpresaId/${empresaId}`)
            .then((response) => {
                const gerenteFotografo: GerenteFotografoType[] = response.data;
                setListGerenteFotografoByEmpresa(gerenteFotografo);
                setLoading(false);
                return gerenteFotografo;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao buscar informações das relações de gerente com fotógrafo. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao buscar informações das relações de gerente com fotógrafo:", error);
                return null;
            });
    }

    function handleGetFotografosByGerente(gerenteId: number): Promise<TerceiroType[] | null> {
        setLoading(true);
        return api
            .get(`/GerenteFotografo/getFotografosByGerente/${gerenteId}`)
            .then((response) => {
                const list = response.data as TerceiroType[];
                setLoading(false);
                return list;
            })
            .catch((error) => {
                console.error("Erro ao buscar fotógrafos do gerente:", error);
                setLoading(false);
                return null;
            });
    }

    return {
        createGerenteFotografo,
        getAllGerenteFotografoPaged,
        updateGerenteFotografo,
        deleteGerenteFotografo,
        setListGerenteFotografo,
        listGerenteFotografo,
        setGerenteFotografoPageCount,
        gerenteFotografoPageCount,
        setInitialListGerenteFotografo,
        initialListGerenteFotografo,
        setGerenteFotografo,
        gerenteFotografo,
        handleGetGerenteFotografoByEmpresaId,
        setListGerenteFotografoByEmpresa,
        listGerenteFotografoByEmpresa,
        handleGetFotografosByGerente
    };
}